import { Switch } from "@headlessui/react";
import clsx from "clsx";
import { MdOutlineEdit } from "react-icons/md";

import { BlankSingleSideEnum, type BlankProductData } from "../../interfaces/blank";
import { formatCurrency } from "../../utils/currency";
import { LoadingSkeleton } from "../LoadingSkeleton";

import { BlankImage, type Props as BlankImageProps } from "../BlankImage";
import { ColorOptions } from "./ColorOptions";
import { SizeOptions } from "./SizeOptions";

export type SimpleBlankProduct = Pick<BlankProductData, "id" | "name" | "price" | "imageSrc" | "colors"> &
  Partial<BlankProductData> & {
    printName?: string;
    customizationSettings?: {
      [k in BlankSingleSideEnum]?: BlankImageProps["customization"];
    };
    multipleImageSrc?: string[][];
    selected?: boolean;
  };

export interface Props {
  item: SimpleBlankProduct;
  className?: string;
  loading?: boolean;
  hasStartingPrice?: boolean;
  hideSizes?: boolean;
  isPreview?: boolean;
  colorOptionsOverflowTextClassName?: string;
  onClick?: (item: SimpleBlankProduct) => void;
  onEdit?: (item: SimpleBlankProduct) => void;
  onLoadImage?: (imageArea: { w: number; h: number }, blankId: string) => void;
}

export const BlankProduct = ({
  onClick,
  onEdit,
  onLoadImage,
  item,
  className,
  loading,
  hasStartingPrice,
  hideSizes,
  isPreview = false,
  colorOptionsOverflowTextClassName,
}: Props) => {
  const { id, name, printName, price, imageSrc, colors, sizes, customizationSettings, multipleImageSrc, imageSrc2 } =
    item;

  if (loading) {
    return (
      <div className="flex min-h-[18.25rem] flex-col">
        <LoadingSkeleton kind="image" className="!h-[11rem] w-[9.75rem] sm:!h-[20.25rem] sm:w-[18rem]" />
        <LoadingSkeleton kind="paragraph" className="mt-2 max-w-[8.5rem]" />
        <LoadingSkeleton kind="colorOptions" className="mt-1" />
        <LoadingSkeleton kind="title" className="mt-1 max-w-[8.5rem]" />
      </div>
    );
  }

  return (
    <div className={clsx("flex w-[9.75rem] flex-col gap-2 bg-base-white sm:w-[18rem]", className)}>
      <div
        className={clsx(
          "relative flex items-center justify-center border-b-[2px]",
          isPreview ? "cursor-default" : "cursor-pointer",
          item.selected ? "border-brand-accent" : "border-base-input",
        )}
        onClick={() => onClick?.(item)}
      >
        {onEdit && (
          <div className="absolute top-1 z-20 flex w-full items-center justify-between px-2">
            <Switch
              checked={item.selected}
              className={
                "group inline-flex h-[1.25rem] w-[2.25rem] items-center rounded-full " +
                "bg-base-gray transition data-[checked]:bg-brand-accent"
              }
            >
              <span
                className={
                  "size-2 translate-x-[2px] rounded-full bg-base-white transition " +
                  "group-data-[checked]:translate-x-[1.125rem]"
                }
              />
            </Switch>

            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(item);
              }}
              className="flex h-5 w-5 min-w-5 items-center justify-center rounded-full border-2 border-base-input"
            >
              <MdOutlineEdit size="1.5rem" />
            </button>
          </div>
        )}

        <BlankImage
          imageSrc={imageSrc}
          imageSrc2={imageSrc2}
          preferLightweightImage
          customization={customizationSettings?.FRONT || customizationSettings?.BACK}
          multipleImageSrc={multipleImageSrc}
          onLoadImage={onLoadImage && ((imageArea) => onLoadImage(imageArea, id))}
          className="!h-[11rem] sm:!h-[20.25rem]"
          sizes="(max-width: 640px) 192px, 320px"
        />
      </div>

      <div className="flex flex-col justify-between gap-1 text-sm font-normal sm:text-base">
        <div className="flex flex-col gap-1">
          <p className="flex flex-col">
            {printName && <span className="font-bold leading-6">{printName}</span>}
            <span className="leading-6">{name}</span>
          </p>
          {colors && (
            <ColorOptions
              colors={colors}
              isPreview={isPreview}
              overflowTextClassName={colorOptionsOverflowTextClassName}
            />
          )}
          {!hideSizes && sizes && <SizeOptions sizes={sizes.map((size) => ({ text: size, enabled: true }))} />}
        </div>

        <div className="flex items-center gap-1">
          {hasStartingPrice && "A partir de "}

          <span className={"text-[1.25rem] font-bold leading-[1.875rem] lg:text-[1.5rem] lg:leading-[2.5rem]"}>
            {formatCurrency(price)}
          </span>
        </div>
      </div>
    </div>
  );
};
